import React from 'react';
import { useTranslation } from 'react-i18next';

import { WizardLayout } from '../../components/wizard';
import { useConfigurator } from '../../store/configurator';
import { DevicePreview } from '../finish/components/DevicePreview';
import { BillingSummary, BillingFooter } from '../finish/components/Billing';
import { isOneTimeBilling } from '../../data/billing';

/**
 * v0 Step 3 — Payment review. Shows the configured site on the laptop+phone group with
 * the billing summary; "Continue to payment" advances to the Stripe payment step.
 *
 * v3's Finish step folded review + checkout into one screen (an in-page overlay); v0
 * splits them into two wizard steps. The Features (packs) and Players (audience) steps are
 * skipped in this flow, so the total here is the base setup price with the Basic feature
 * baseline (committed as defaults by the store).
 */
export const ReviewStep = (): React.ReactElement => {
  const { t } = useTranslation();
  // Billing is derived reactively from the live session in the store; it updates as the
  // latest selection's `sessionSave` lands, so the total tracks the selections.
  const { billing } = useConfigurator();
  const symbol = billing?.symbol ?? '$';
  const currencyCode = billing?.currency ?? 'USD';

  return (
    <WizardLayout
      currentStep={2}
      title={t('Review & Pay')}
      description={t('Your brand is set to launch! Take a moment to review your brand setup and investment information.')}
      main={<DevicePreview />}
      footerExtra={
        <BillingFooter
          symbol={symbol}
          currencyCode={currencyCode}
          total={billing?.dueToday ?? null}
          oneTime={isOneTimeBilling(billing)}
        />
      }
    >
      <BillingSummary billing={billing} symbol={symbol} currencyCode={currencyCode} editableVoucher />
    </WizardLayout>
  );
};

export default ReviewStep;
